import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/v2`;

export default 
// Subscriptions Tab Component
function SubscriptionsTab({ onRefresh, organizations }) {
  const [subscriptions, setSubscriptions] = useState([]);
  const [orgsWithoutSub, setOrgsWithoutSub] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreateSub, setShowCreateSub] = useState(null);
  const [showEditSub, setShowEditSub] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [createForm, setCreateForm] = useState({ tier: 'start', is_trial: true, trial_days: 14, send_notification: false });
  const [expandedHistory, setExpandedHistory] = useState(null);
  const [historyData, setHistoryData] = useState({});
  
  useEffect(() => {
    fetchSubscriptions();
  }, []);
  
  const fetchSubscriptions = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('systemklart_token');
      const res = await axios.get(`${API}/ops/system/subscriptions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubscriptions(res.data.subscriptions);
      setOrgsWithoutSub(res.data.orgs_without_subscription);
      setSummary(res.data.summary);
    } catch (error) {
      toast.error('Kunne ikke hente abonnementer');
    } finally {
      setLoading(false);
    }
  };
  
  const createSubscription = async (orgId) => {
    try {
      const token = localStorage.getItem('systemklart_token');
      await axios.post(`${API}/ops/system/subscriptions/create`, {
        org_id: orgId,
        tier: createForm.tier,
        status: 'active',
        is_trial: createForm.is_trial,
        trial_days: createForm.trial_days,
        send_notification: createForm.send_notification
      }, { headers: { Authorization: `Bearer ${token}` } });
      
      toast.success(createForm.send_notification ? 'Abonnement opprettet og varslet sendt' : 'Abonnement opprettet');
      setShowCreateSub(null);
      setCreateForm({ tier: 'start', is_trial: true, trial_days: 14, send_notification: false });
      fetchSubscriptions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Kunne ikke opprette abonnement');
    }
  };
  
  const updateSubscription = async (orgId) => {
    try {
      const token = localStorage.getItem('systemklart_token');
      await axios.put(`${API}/ops/system/subscriptions/${orgId}`, {
        ...editForm,
        send_notification: editForm.send_notification || false
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success(editForm.send_notification ? 'Abonnement oppdatert og varslet sendt' : 'Abonnement oppdatert');
      setShowEditSub(null);
      fetchSubscriptions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Kunne ikke oppdatere abonnement');
    }
  };
  
  const convertToTrial = async (orgId) => {
    const sendEmail = window.confirm('Vil du sende e-postvarsling til kundens admin-brukere om denne endringen?');
    try {
      const token = localStorage.getItem('systemklart_token');
      await axios.post(`${API}/ops/system/subscriptions/${orgId}/convert-to-trial`, {
        trial_days: 14,
        send_notification: sendEmail
      }, { headers: { Authorization: `Bearer ${token}` } });
      
      toast.success(sendEmail ? 'Konvertert til testbruker og varslet sendt' : 'Konvertert til testbruker');
      fetchSubscriptions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Kunne ikke konvertere');
    }
  };
  
  const deleteSubscription = async (orgId) => {
    if (!window.confirm('Er du sikker på at du vil slette abonnementet?')) return;
    const sendEmail = window.confirm('Vil du sende e-postvarsling til kundens admin-brukere?');
    
    try {
      const token = localStorage.getItem('systemklart_token');
      await axios.delete(`${API}/ops/system/subscriptions/${orgId}`, {
        headers: { Authorization: `Bearer ${token}` },
        data: { send_notification: sendEmail }
      });
      
      toast.success(sendEmail ? 'Abonnement slettet og varslet sendt' : 'Abonnement slettet');
      fetchSubscriptions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Kunne ikke slette');
    }
  };
  
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('nb-NO');
  };
  
  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    const d = new Date(dateStr);
    return `${d.toLocaleDateString('nb-NO')} ${d.toLocaleTimeString('nb-NO', { hour: '2-digit', minute: '2-digit' })}`;
  };
  
  const toggleHistory = async (orgId) => {
    if (expandedHistory === orgId) {
      setExpandedHistory(null);
      return;
    }
    setExpandedHistory(orgId);
    if (!historyData[orgId]) {
      try {
        const token = localStorage.getItem('systemklart_token');
        const res = await axios.get(`${API}/ops/system/subscriptions/${orgId}/history`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setHistoryData(prev => ({ ...prev, [orgId]: res.data.history }));
      } catch {
        setHistoryData(prev => ({ ...prev, [orgId]: [] }));
      }
    }
  };
  
  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-green-500/20 text-green-400';
      case 'trial': return 'bg-blue-500/20 text-blue-400';
      case 'suspended': return 'bg-yellow-500/20 text-yellow-400';
      case 'expired': return 'bg-red-500/20 text-red-400';
      case 'cancelled': return 'bg-gray-500/20 text-gray-400';
      default: return 'bg-gray-500/20 text-gray-400';
    }
  };
  
  const getTierColor = (tier) => {
    switch (tier) {
      case 'pro': return 'bg-purple-500/20 text-purple-400';
      case 'start': return 'bg-cyan-500/20 text-cyan-400';
      case 'enterprise': return 'bg-amber-500/20 text-amber-400';
      default: return 'bg-gray-500/20 text-gray-400';
    }
  };
  
  if (loading) return <div className="text-center py-8 text-gray-400">Laster...</div>;
  
  return (
    <div className="space-y-6" data-testid="subscriptions-tab">
      {/* Summary cards */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4" data-testid="subscriptions-summary">
          <div className="bg-gray-800 rounded-lg p-4">
            <div className="text-2xl font-bold text-white" data-testid="summary-total">{summary.total}</div>
            <div className="text-sm text-gray-400">Totalt</div>
          </div>
          <div className="bg-gray-800 rounded-lg p-4">
            <div className="text-2xl font-bold text-green-400" data-testid="summary-active">{summary.active}</div>
            <div className="text-sm text-gray-400">Aktive</div>
          </div>
          <div className="bg-gray-800 rounded-lg p-4">
            <div className="text-2xl font-bold text-blue-400" data-testid="summary-trial">{summary.trial}</div>
            <div className="text-sm text-gray-400">Prøveperiode</div>
          </div>
          <div className="bg-gray-800 rounded-lg p-4">
            <div className="text-2xl font-bold text-yellow-400" data-testid="summary-suspended">{summary.suspended}</div>
            <div className="text-sm text-gray-400">Suspendert</div>
          </div>
          <div className="bg-gray-800 rounded-lg p-4">
            <div className="text-2xl font-bold text-gray-400" data-testid="summary-no-sub">{summary.no_subscription}</div>
            <div className="text-sm text-gray-400">Uten abonnement</div>
          </div>
        </div>
      )}
      
      {/* Orgs without subscription */}
      {orgsWithoutSub.length > 0 && (
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4">
          <h3 className="font-semibold text-amber-400 mb-3">Organisasjoner uten abonnement ({orgsWithoutSub.length})</h3>
          <div className="space-y-2">
            {orgsWithoutSub.map(org => (
              <div key={org.id} className="flex items-center justify-between bg-gray-800/50 rounded-lg p-3" data-testid={`org-without-sub-${org.id}`}>
                <div>
                  <div className="font-medium text-white">{org.name}</div>
                  <div className="text-xs text-gray-500">Org.nr: {org.orgnr}</div>
                </div>
                <button
                  onClick={() => setShowCreateSub(org)}
                  className="px-3 py-1.5 bg-cyan-600 text-white text-sm rounded-lg hover:bg-cyan-700"
                  data-testid={`create-sub-btn-${org.id}`}
                >
                  Opprett abonnement
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
      
      {/* Subscriptions list */}
      <div className="bg-gray-800 rounded-lg overflow-hidden">
        <div className="p-4 border-b border-gray-700 flex justify-between items-center">
          <h2 className="text-lg font-semibold text-white">Abonnementer</h2>
          <button onClick={fetchSubscriptions} className="text-sm text-cyan-400 hover:text-cyan-300">
            Oppdater
          </button>
        </div>
        
        <div className="divide-y divide-gray-700" data-testid="subscriptions-list">
          {subscriptions.map(sub => (
            <div key={sub.id} className="hover:bg-gray-700/30" data-testid={`subscription-row-${sub.organization_id}`}>
              <div className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-semibold text-white" data-testid={`sub-org-name-${sub.organization_id}`}>{sub.org_name}</span>
                      <span className={`px-2 py-0.5 text-xs rounded-full ${getTierColor(sub.tier)}`} data-testid={`sub-tier-${sub.organization_id}`}>
                        {sub.tier_name || sub.tier}
                      </span>
                      <span className={`px-2 py-0.5 text-xs rounded-full ${getStatusColor(sub.status)}`} data-testid={`sub-status-${sub.organization_id}`}>
                        {sub.status}
                      </span>
                      {sub.is_trial && (
                        <span className="px-2 py-0.5 text-xs rounded-full bg-blue-500/20 text-blue-400">
                          Prøveperiode
                        </span>
                      )}
                    </div>
                    <div className="text-sm text-gray-400 space-x-4">
                      <span>Org.nr: {sub.org_number}</span>
                      <span>Pris: {sub.price} kr/mnd</span>
                      <span>Utløper: {formatDate(sub.end_date)}</span>
                      {sub.is_trial && sub.trial_ends_at && (
                        <span className="text-blue-400">Prøveperiode til: {formatDate(sub.trial_ends_at)}</span>
                      )}
                    </div>
                    {sub.admin_notes && (
                      <div className="text-xs text-gray-500 mt-1">Notat: {sub.admin_notes}</div>
                    )}
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => toggleHistory(sub.organization_id)}
                      className={`px-3 py-1.5 text-sm rounded-lg transition-colors ${
                        expandedHistory === sub.organization_id 
                          ? 'bg-amber-600/20 text-amber-400 border border-amber-500/30' 
                          : 'bg-gray-700/50 text-gray-400 hover:text-white hover:bg-gray-700'
                      }`}
                      data-testid={`history-btn-${sub.organization_id}`}
                    >
                      Historikk
                    </button>
                    <button
                      onClick={() => { setShowEditSub(sub); setEditForm({}); }}
                      className="px-3 py-1.5 bg-gray-700 text-white text-sm rounded-lg hover:bg-gray-600"
                      data-testid={`edit-sub-btn-${sub.organization_id}`}
                    >
                      Rediger
                    </button>
                    {!sub.is_trial && (
                      <button
                        onClick={() => convertToTrial(sub.organization_id)}
                        className="px-3 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700"
                        data-testid={`convert-trial-btn-${sub.organization_id}`}
                      >
                        &rarr; Testbruker
                      </button>
                    )}
                    <button
                      onClick={() => deleteSubscription(sub.organization_id)}
                      className="px-3 py-1.5 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700"
                      data-testid={`delete-sub-btn-${sub.organization_id}`}
                    >
                      Slett
                    </button>
                  </div>
                </div>
              </div>
              
              {/* Change History */}
              {expandedHistory === sub.organization_id && (
                <div className="px-4 pb-4" data-testid={`history-panel-${sub.organization_id}`}>
                  <div className="bg-gray-900/60 rounded-lg border border-gray-700/50 p-4">
                    <h4 className="text-sm font-semibold text-amber-400 mb-3">Endringshistorikk</h4>
                    {!historyData[sub.organization_id] ? (
                      <div className="text-sm text-gray-500">Laster...</div>
                    ) : historyData[sub.organization_id].length === 0 ? (
                      <div className="text-sm text-gray-500">Ingen endringer registrert</div>
                    ) : (
                      <div className="space-y-3 max-h-64 overflow-y-auto">
                        {historyData[sub.organization_id].map((entry, idx) => (
                          <div key={idx} className="flex gap-3 text-sm">
                            <div className="flex-shrink-0 w-2 h-2 mt-1.5 rounded-full bg-cyan-500"></div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-0.5">
                                <span className="text-gray-400 text-xs">{formatDateTime(entry.timestamp)}</span>
                                <span className="text-gray-600 text-xs">av {entry.changed_by}</span>
                                {entry.action && (
                                  <span className={`px-1.5 py-0.5 text-xs rounded ${
                                    entry.action === 'created' ? 'bg-green-500/20 text-green-400' :
                                    entry.action === 'converted_to_trial' ? 'bg-blue-500/20 text-blue-400' :
                                    'bg-gray-600/30 text-gray-400'
                                  }`}>{
                                    entry.action === 'created' ? 'Opprettet' :
                                    entry.action === 'converted_to_trial' ? 'Testbruker' :
                                    'Oppdatert'
                                  }</span>
                                )}
                              </div>
                              <ul className="text-gray-300 space-y-0.5">
                                {(entry.changes || []).map((change, ci) => (
                                  <li key={ci} className="text-xs">{change}</li>
                                ))}
                              </ul>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
      
      {/* Create Subscription Modal */}
      {showCreateSub && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-testid="create-subscription-modal">
          <div className="bg-gray-800 rounded-xl p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-white mb-4">
              Opprett abonnement for {showCreateSub.name}
            </h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm text-gray-400 mb-1">Abonnementstype</label>
                <select
                  value={createForm.tier}
                  onChange={e => setCreateForm({...createForm, tier: e.target.value})}
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white"
                  data-testid="create-sub-tier-select"
                >
                  <option value="start">Start (299 kr/mnd)</option>
                  <option value="pro">Pro (549 kr/mnd)</option>
                </select>
              </div>
              
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="is_trial"
                  checked={createForm.is_trial}
                  onChange={e => setCreateForm({...createForm, is_trial: e.target.checked})}
                  className="w-4 h-4"
                  data-testid="create-sub-trial-checkbox"
                />
                <label htmlFor="is_trial" className="text-sm text-gray-300">Prøveperiode (gratis)</label>
              </div>
              
              {createForm.is_trial && (
                <div>
                  <label className="block text-sm text-gray-400 mb-1">Varighet (dager)</label>
                  <input
                    type="number"
                    value={createForm.trial_days}
                    onChange={e => setCreateForm({...createForm, trial_days: parseInt(e.target.value)})}
                    className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white"
                    data-testid="create-sub-trial-days"
                  />
                </div>
              )}
              
              <div className="flex items-center gap-3 pt-2 border-t border-gray-700">
                <input
                  type="checkbox"
                  id="create_send_notification"
                  checked={createForm.send_notification}
                  onChange={e => setCreateForm({...createForm, send_notification: e.target.checked})}
                  className="w-4 h-4 accent-cyan-500"
                  data-testid="create-sub-notify-checkbox"
                />
                <label htmlFor="create_send_notification" className="text-sm text-gray-300">
                  Send e-postvarsling til kundens admin-brukere
                </label>
              </div>
              
              <div className="flex gap-3 pt-4">
                <button
                  onClick={() => createSubscription(showCreateSub.id)}
                  className="flex-1 bg-cyan-600 text-white py-2 rounded-lg hover:bg-cyan-700"
                  data-testid="create-sub-submit-btn"
                >
                  Opprett
                </button>
                <button
                  onClick={() => setShowCreateSub(null)}
                  className="flex-1 bg-gray-700 text-white py-2 rounded-lg hover:bg-gray-600"
                  data-testid="create-sub-cancel-btn"
                >
                  Avbryt
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Edit Subscription Modal */}
      {showEditSub && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-testid="edit-subscription-modal">
          <div className="bg-gray-800 rounded-xl p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-white mb-4">
              Rediger abonnement: {showEditSub.org_name}
            </h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm text-gray-400 mb-1">Abonnementstype</label>
                <select
                  value={editForm.tier || showEditSub.tier}
                  onChange={e => setEditForm({...editForm, tier: e.target.value})}
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white"
                  data-testid="edit-sub-tier-select"
                >
                  <option value="start">Start (299 kr/mnd)</option>
                  <option value="pro">Pro (549 kr/mnd)</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm text-gray-400 mb-1">Status</label>
                <select
                  value={editForm.status || showEditSub.status}
                  onChange={e => setEditForm({...editForm, status: e.target.value})}
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white"
                  data-testid="edit-sub-status-select"
                >
                  <option value="active">Aktiv</option>
                  <option value="suspended">Suspendert</option>
                  <option value="expired">Utl&oslash;pt</option>
                  <option value="cancelled">Avsluttet</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm text-gray-400 mb-1">Forleng med dager</label>
                <input
                  type="number"
                  placeholder="f.eks. 30"
                  value={editForm.extend_days || ''}
                  onChange={e => setEditForm({...editForm, extend_days: parseInt(e.target.value) || null})}
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white"
                  data-testid="edit-sub-extend-days"
                />
              </div>
              
              <div>
                <label className="block text-sm text-gray-400 mb-1">Admin-notat</label>
                <textarea
                  value={editForm.notes ?? showEditSub.admin_notes ?? ''}
                  onChange={e => setEditForm({...editForm, notes: e.target.value})}
                  className="w-full bg-gray-700 border border-gray-600 rounded-lg px-3 py-2 text-white h-20"
                  placeholder="Internt notat..."
                  data-testid="edit-sub-notes"
                />
              </div>
              
              <div className="flex items-center gap-3 pt-2 border-t border-gray-700">
                <input
                  type="checkbox"
                  id="edit_send_notification"
                  checked={editForm.send_notification || false}
                  onChange={e => setEditForm({...editForm, send_notification: e.target.checked})}
                  className="w-4 h-4 accent-cyan-500"
                  data-testid="edit-sub-notify-checkbox"
                />
                <label htmlFor="edit_send_notification" className="text-sm text-gray-300">
                  Send e-postvarsling til kundens admin-brukere
                </label>
              </div>
              
              <div className="flex gap-3 pt-4">
                <button
                  onClick={() => updateSubscription(showEditSub.organization_id)}
                  className="flex-1 bg-cyan-600 text-white py-2 rounded-lg hover:bg-cyan-700"
                  data-testid="edit-sub-save-btn"
                >
                  Lagre
                </button>
                <button
                  onClick={() => setShowEditSub(null)}
                  className="flex-1 bg-gray-700 text-white py-2 rounded-lg hover:bg-gray-600"
                  data-testid="edit-sub-cancel-btn"
                >
                  Avbryt
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

